/**
 * HomeDashboardContainer
 *
 *
 */

import { useState, useEffect } from 'react';
import HomeDashboardPresenter from './HomeDashboardPresenter';
import MessageAlert, { isMobile } from 'utils';
import {
  StatisticsApi,
  InquiryApi,
  ReviewApi,
  RefundApi,
  ParcelServiceApi,
} from 'api';
import moment from 'moment';
import Logger from 'utils/logger';

// 조회할 기간
const DAY = -10;
// const DAY = -30;
const MOMENT_FORMAT = 'YYYY-MM-DD';
const startDate = moment().add(DAY, 'days').format(MOMENT_FORMAT);
const endDate = moment().format(MOMENT_FORMAT);

const logger = new Logger('HomeDashboardContainer');

/**----------------------------------------
 * [Component] 홈
 * ----------------------------------------
 */
const HomeDashboardContainer = ({ defaultShopId, permission, shopType }) => {
  /* ===== INIT ===== */

  /* ===== STATE ===== */
  const [currentShop, _setCurrentShop] = useState(defaultShopId ?? null);
  const [loading, setLoading] = useState(false);
  const [noticeList, setNoticeList] = useState([]);
  const [orderList, setOrderList] = useState(null);
  const [storeSales, setStoreSales] = useState(null);
  const [storeSales2, setStoreSales2] = useState(null);
  const [reviewList, setReviewList] = useState([]);
  const [inquiryList, setInquiryList] = useState([]);
  const [refundList, setRefundList] = useState([]);
  const [isMobileCheck, setIsMobileCheck] = useState(false);
  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /**--------------------
   * 일별 주문내역 조회
   * --------------------
   */
  const getOrderList = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        `parcel_service`,
        {
          startDate,
          endDate,
          ...(currentShop && { shopId: currentShop }),
        },
      );
      if (status !== 200) throw { message };
      setOrderList(data?.parcelSale);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 데이터 조회
   * --------------------
   */
  const getStoreSales = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        'daily',
        {
          startDate,
          endDate,
          ...(currentShop && { shopId: currentShop }),
        },
      );
      if (status !== 200) throw { message };
      setStoreSales(data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 데이터 조회
   * --------------------
   */
  const getShopInformationSales = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        'shop',
        {
          // startDate: endDate,
          startDate,
          endDate,
          ...(currentShop && { shopId: currentShop }),
        },
      );
      if (status !== 200) throw { message };
      setStoreSales2(data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 문의목록 조회
   * --------------------
   */
  const handleGetInquiryList = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await InquiryApi.getInquiries({
        ...(currentShop && { shop_information_id: currentShop }),
        join_tables: `["user", "product"]`,
      });
      if (status !== 200) throw { message };

      setTimeout(() => {
        setInquiryList(
          data.filter(
            (item) =>
              item.inquiry_type !== 'FAQ' && item.inquiry_answer_id === null,
          ),
        );
        setLoading(false);
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 리뷰목록 조회
   * --------------------
   */
  const getReviewList = async () => {
    try {
      const { status, message, data } = await ReviewApi.getReviews({
        ...(currentShop && { shop_information_id: currentShop }),
        // start_date: startDateUnix,
        // end_date: endDateUnix,
        join_tables: `["user", "product"]`,
      });
      if (status !== 200) throw { message };
      setReviewList(data.filter((item) => item.is_show === null));
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 환불목록 조회
   * --------------------
   */
  const getRefundList = async () => {
    try {
      const { status, message, data } = await RefundApi.getRefunds({
        ...(currentShop && { shop_information_id: currentShop }),
      });

      if (status !== 200) {
        logger.warn('getRefundList', message);
        return;
      }

      const filtered = data.filter(
        (_item) =>
          _item.refund_status === 'PENDING' ||
          _item.refund_status === 'APPROVED',
      );

      // 대시보드에서 무통장입금 환불대기 건만 구분할 수 있도록 결제수단을 보완합니다.
      const refundListWithPaymentMethod = await Promise.all(
        filtered.map(async (refund) => {
          if (!refund?.parcel_service_id) return refund;

          try {
            const parcelResult = await ParcelServiceApi.getParcelService(
              refund.parcel_service_id,
              { join_tables: '["shop_order"]' },
            );

            return {
              ...refund,
              payment_method:
                parcelResult?.data?.payment_method ??
                parcelResult?.data?.shop_order?.payment_method,
            };
          } catch (err) {
            // 결제수단 조회 실패가 기존 환불 목록 노출을 막지 않도록 원본 데이터를 유지합니다.
            logger.warn('getRefundPaymentMethod', err);
            return refund;
          }
        }),
      );

      setRefundList(refundListWithPaymentMethod);
    } catch (err) {
      logger.error('getRefundList', err);
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    getOrderList();
    getStoreSales();
    getShopInformationSales();
    handleGetInquiryList();
    getReviewList();
    getRefundList();
  }, []);

  useEffect(() => {
    setIsMobileCheck(isMobile());
  }, []);

  /* ===== RENDER ===== */
  const props = {
    loading,
    startDate,
    endDate,
    noticeList,
    orderList,
    storeSales,
    storeSales2,
    reviewList,
    inquiryList,

    permission,
    shopType,
    // chartData,
    // permission,
    // orderList,
    // productList,
    // inquiryList,
    refundList,
    isMobile: isMobileCheck,
  };
  // JSX: start
  return (
    <>
      <HomeDashboardPresenter {...props} />
    </>
  ); // JSX: end
};

HomeDashboardContainer.defaultProps = {
  defaultShopId: null,
  shopType: null,
  permission: 'ADMIN',
};
export default HomeDashboardContainer;
